/**
 * "Load more" on the results page — one group, keyset-paged (`GET /search`, §5.6).
 *
 * The first screen comes from the stream; this runs only once somebody asks for more of one
 * group, and it is enabled per group, so a page of six groups is never six extra requests.
 * The key carries the query and the group — the cursor is bound to both (`KLQ-20260821-003`
 * rule 3), and a cursor replayed under another query would be `400 cursor_invalid` — and the
 * workspace the tab acts as, because a search answers per acting company (`features/studio`'s
 * rule: the header and the key move together or the cache would lie).
 *
 * Not live: a search is an answer to a question asked once. No refetch on focus, no
 * invalidation from anywhere.
 */

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import { SESSION_QUERY_KEY, useActiveWorkspaceId } from '@/features/session';

import { searchPage } from './api';
import type { SearchGroupKey } from './api';

const SEARCH_KEY = [...SESSION_QUERY_KEY, 'search'] as const;

export function searchGroupQueryOptions(
  workspaceId: string | null,
  q: string,
  group: SearchGroupKey,
) {
  return infiniteQueryOptions({
    queryKey: [...SEARCH_KEY, workspaceId, q, group] as const,
    queryFn: ({ pageParam }) => searchPage(q, group, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => (page.has_more ? (page.next_cursor ?? null) : null),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useSearchGroup(q: string, group: SearchGroupKey, enabled: boolean) {
  const workspaceId = useActiveWorkspaceId();
  return useInfiniteQuery({
    ...searchGroupQueryOptions(workspaceId, q, group),
    enabled: enabled && q !== '',
  });
}
